import {
  Box,
  DrawerCloseButton,
  DrawerContent,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { CellValue } from 'react-table';

import { IconBan, IconPen, IconTrashbin, IconThunder } from 'assets/icons';
import Button from 'components/Button';
import Drawer, { ControllableDrawer } from 'components/Drawer';
import { ConfirmationModal } from 'components/Modal';
import SearchField from 'components/SearchField';
import Table from 'components/Table';
import Title from 'components/Title';
import AddBreak from 'containers/CompanySettings/RightDrawer/AddBreak';
import { initialBreakData } from 'containers/CompanySettings/RightDrawer/AddBreak/schema';
import { routePaths } from 'global/routePaths';
import { useActivateBreakRules } from 'hooks/breakRules/useActivateBreakRules';
import { useDeactivateBreakRules } from 'hooks/breakRules/useDeactivateBreakRules';
import { useDeleteBreakRules, IDeleteBreakRules } from 'hooks/breakRules/useDeleteBreakRules';
import { useFetchPaginatedBreakRules, IBreakSearchQuery } from 'hooks/breakRules/useFetchBreakRules';
import { useCompanyInfo } from 'store/useUserStore';

const initialSearchQuery = {
  page: 1,
  page_size: 10,
  search: '',
};

const Breaks: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const companyInfo = useCompanyInfo();

  const [searchQuery, setSearchQuery] = useState<any>(initialSearchQuery);
  const [formData, setFormData] = useState<typeof initialBreakData>(initialBreakData);
  const [dialogIds, setDialogIds] = useState<IDeleteBreakRules>({ break_rule_ids: [] });

  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const {
    isOpen: isDeactivateModalOpen,
    onOpen: onDeactivateModalOpen,
    onClose: onDeactivateModalClose,
  } = useDisclosure();
  const { isOpen: isActivateModalOpen, onOpen: onActivateModalOpen, onClose: onActivateModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { status: useActivateBreakStatus, mutateAsync: mutateAsyncActivate } = useActivateBreakRules();
  const { status: useDeactivateBreakStatus, mutateAsync: mutateAsyncDeactivate } = useDeactivateBreakRules();
  const { status: useDeleteBreakStatus, mutateAsync: mutateAsyncDelete } = useDeleteBreakRules();
  const { status: breaksRulesStatus, data: breaksRulesData, refetch } = useFetchPaginatedBreakRules(searchQuery);

  const columns = useMemo(
    () => [
      {
        Header: t('common:name'),
        accessor: 'name',
        Cell: (data: CellValue) => {
          return (
            <Text fontWeight="semibold" color="primary.500">
              {data.row.original?.name}{' '}
              {/* {(data.row.original?.is_cali_meal_break || data.row.original?.is_cali_rest_break) && (
                <Badge
                  alignSelf="flex-start"
                  variant="solid"
                  bg="secondary.400"
                  color="white"
                  px="2"
                  py="0.5"
                  fontWeight="small"
                  textTransform="uppercase"
                  borderRadius="8px"
                >
                  {data.row.original?.is_cali_meal_break ? t('common:caliMealBreak') : t('common:caliRestBreak')}
                </Badge>
              )} */}
            </Text>
          );
        },
      },
      {
        Header: t('common:type'),
        accessor: 'paid',
        Cell: (data: CellValue) => {
          return (
            <Text fontWeight="semibold" color="primary.500">
              {data.row.original?.paid ? t('common:paid') : t('common:unpaid')}
            </Text>
          );
        },
      },
      {
        Header: 'Status',
        accessor: 'active',
        Cell: (data: CellValue) => {
          return (
            <Text fontWeight="semibold" color="primary.500">
              {data.row.original?.active ? t('common:active') : t('common:inactive')}{' '}
            </Text>
          );
        },
      },
      {
        Header: '',
        accessor: 'action',
        Cell: (data: CellValue) => {
          return (
            <Box>
              <Tooltip label={t('companySettings:updateBreak')} hasArrow bg="black">
                <IconButton
                  aria-label={t('companySettings:updateBreak')}
                  bg="rgba(38, 103, 201, 0.1)"
                  icon={<Icon as={IconPen} />}
                  size="sm"
                  mr="3"
                  _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                  onClick={() => handleEditIconClick(data.row.original)}
                />
              </Tooltip>
              {data.row.original?.active ? (
                <Tooltip label={t('companySettings:deactivateBreak')} hasArrow bg="black">
                  <IconButton
                    aria-label={t('companySettings:deactivateBreak')}
                    bg="rgba(101, 104, 134, 0.1)"
                    icon={<Icon as={IconBan} />}
                    size="sm"
                    mr="3"
                    _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                    onClick={() => handleDeactivateIconClick(data.row.original?.id)}
                  />
                </Tooltip>
              ) : (
                <Tooltip label={t('companySettings:activateBreak')} hasArrow bg="black">
                  <IconButton
                    aria-label={t('companySettings:activateBreak')}
                    bg="rgba(101, 104, 134, 0.1)"
                    icon={<Icon as={IconThunder} />}
                    size="sm"
                    mr="3"
                    _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                    onClick={() => handleActivateIconClick(data.row.original?.id)}
                  />
                </Tooltip>
              )}
              <Tooltip label={t('companySettings:deleteBreak')} hasArrow bg="black">
                <IconButton
                  aria-label={t('companySettings:deleteBreak')}
                  bg="rgba(255, 83, 83, 0.1)"
                  icon={<Icon as={IconTrashbin} />}
                  size="sm"
                  _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  onClick={() => handleDeleteIconClick(data.row.original?.id)}
                />
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handleEditIconClick = (breakData: typeof initialBreakData) => {
    setFormData(breakData);
    onDrawerOpen();
  };

  const handleDrawerClose = () => {
    setFormData(initialBreakData);
    onDrawerClose();
  };

  const handleDeactivateIconClick = (id: number) => {
    setDialogIds({ break_rule_ids: [id] });
    onDeactivateModalOpen();
  };

  const handleActivateIconClick = (id: number) => {
    setDialogIds({ break_rule_ids: [id] });
    onActivateModalOpen();
  };

  const handleDeleteIconClick = (id: number) => {
    setDialogIds({ break_rule_ids: [id] });
    onDeleteModalOpen();
  };

  const deactivateBreak = () => {
    mutateAsyncDeactivate(dialogIds).then(() => {
      refetch();
      onDeactivateModalClose();
      setDialogIds({ break_rule_ids: [] });
    });
  };

  const activateBreak = () => {
    mutateAsyncActivate(dialogIds).then(() => {
      refetch();
      onActivateModalClose();
      setDialogIds({ break_rule_ids: [] });
    });
  };

  const deleteBreak = () => {
    mutateAsyncDelete(dialogIds).then(() => {
      refetch();
      onDeleteModalClose();
      setDialogIds({ break_rule_ids: [] });
    });
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IBreakSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  if (!companyInfo?.allowManualBreak) return <Navigate replace to={routePaths.companySettings.general} />;

  return (
    <>
      <ControllableDrawer onClose={handleDrawerClose} isOpen={isDrawerOpen}>
        <DrawerContent>
          <DrawerCloseButton />
          <AddBreak onClose={handleDrawerClose} formData={formData} />
        </DrawerContent>
      </ControllableDrawer>
      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={onDeactivateModalClose}
        title={t('companySettings:deactivateBreakModalTitle')}
        subTitle=""
        confirmButtonLabel={t('common:deactivate')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: useDeactivateBreakStatus === 'loading',
          onClick: deactivateBreak,
        }}
      />
      <ConfirmationModal
        isOpen={isActivateModalOpen}
        onClose={onActivateModalClose}
        title={t('companySettings:activateBreakModalTitle')}
        subTitle=""
        confirmButtonLabel={t('common:activate')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: useActivateBreakStatus === 'loading',
          onClick: activateBreak,
        }}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('companySettings:deleteBreakTitle')}
        subTitle={t('companySettings:deleteBreakSubtitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: useDeleteBreakStatus === 'loading',
          onClick: deleteBreak,
        }}
      />
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton={true}> {t('companySettings:breakSettings')}</Title>
      </Box>
      <Box>
        <Drawer
          drawerTrigger={onOpen => (
            <Button colorScheme="secondary" mt={{ base: '2', md: '5' }} onClick={onOpen}>
              + {t('companySettings:addBreakRule')}
            </Button>
          )}
        >
          {onClose => <AddBreak onClose={onClose} />}
        </Drawer>
        <Box ml="auto" maxWidth="250px" width="100%" mt="3">
          <SearchField
            inputProps={{
              placeholder: t('companySettings:searchBreaks'),
              onChange: handleSearchInputChange,
            }}
          />
        </Box>
        <Table
          columns={columns}
          data={breaksRulesData?.payload || []}
          paginationData={breaksRulesData?.meta_data?.pagination}
          onPageChange={handlePageChange}
          isLoading={breaksRulesStatus === 'loading'}
          emptyTableStateProps={{
            searchText: searchQuery.search,
            description: 'No Break Rules to Show. Create some now!',
          }}
        />
      </Box>
    </>
  );
};
export default Breaks;
